import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import {
  addressSubfieldsOf,
  asAddress,
  asBool,
  asCascade,
  asChoices,
  asDateRange,
  asSingleChoice,
  asText,
  asUploads,
  type RenderableField,
  toRenderableField,
} from '@/features/questionnaires/elements/answers';
import { formatDate } from '@/lib/dateTime';

import { useSessionQuestionnaire } from '../../api/useSessionQuestionnaire';
import {
  KeyValueList,
  KeyValueRow,
  SubCard,
  TwoColumnGrid,
} from '../../components/detail/Accordion';
import { parseQuestionnaireAnswers } from './questionnaire.utils';

/**
 * A completed Questionnaire step's answers, on the admin's session view.
 *
 * ONE CARD PER SECTION, built from the page's own `SubCard` / `KeyValueList` / `KeyValueRow` — the
 * same structure every other step's Detail and Check List cards use. The step used to render nothing
 * here at all: `StepAccordion` resolves its fields from the API catalogue via `returnDataCatalog`, and
 * a questionnaire has no catalogue entry, so `spec` came back null and the body was empty.
 *
 * The section grouping is not decoration — it is how the questionnaire was authored and how the
 * applicant met it, so a reviewer scanning "which section did they answer oddly" needs the same
 * division on screen. A flat list of twenty label/value rows loses it.
 *
 * A label/value list rather than the form: a reviewer is READING answers, and a page of disabled
 * inputs is harder to scan than the values themselves.
 */
export function QuestionnaireAnswersView({
  sessionId,
  stepId,
  inputJson,
}: {
  sessionId: string | undefined;
  stepId: string;
  /** The step's stored input — `{ answers: { <sectionId>: { <fieldId>: value } } }`. */
  inputJson: string | null;
}) {
  // Enabled unconditionally: this only renders inside an OPEN accordion, so the step is already the
  // one being read.
  const { data, isLoading, error } = useSessionQuestionnaire(sessionId, stepId, true);
  const answers = useMemo(() => parseQuestionnaireAnswers(inputJson), [inputJson]);

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-[length:var(--text-14)] text-[#666666]">
        <Loader2 size={14} className="animate-spin" />
        Loading answers…
      </p>
    );
  }

  if (error || !data) {
    return (
      <p className="flex items-start gap-2 rounded-lg bg-[#FFF7E8] px-3 py-2.5 text-[length:var(--text-12)] leading-relaxed text-[#8A5A00]">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          The questionnaire this step used is no longer available, so the questions can't be shown.
          The answers are still stored on the session.
        </span>
      </p>
    );
  }

  const sections = data.sections.filter((s) => s.fields.length > 0);

  if (sections.length === 0) {
    return (
      <p className="text-[length:var(--text-14)] text-[#666666]">
        This questionnaire has no questions.
      </p>
    );
  }

  return (
    // The same two-column grid the rest of the step bodies use, so two short sections sit side by
    // side instead of each taking a full row.
    <TwoColumnGrid>
      {sections.map((section) => (
        <SubCard key={section.id} title={section.title?.trim() || section.name}>
          <KeyValueList>
            {section.fields.map((dto) => {
              const field = toRenderableField(dto);
              return (
                <FieldAnswer key={field.id} field={field} value={answers[section.id]?.[field.id]} />
              );
            })}
          </KeyValueList>
        </SubCard>
      ))}
    </TwoColumnGrid>
  );
}

/**
 * One question's answer — normally a single label/value row, but a dependent-dropdown chain becomes
 * ONE ROW PER LEVEL.
 *
 * WHY THE SPECIAL CASE. A cascade answer is already a list of level/value pairs — `Country: India`,
 * `State: Gujarat`, `District: Vadodara` — and the card it sits in is a list of label/value pairs.
 * Squeezing all three into a single value cell threw that structure away: the first level printed
 * bare with no label at all while the rest appeared beneath it in smaller grey text, so the answer
 * read as a paragraph rather than as data, and nothing lined up with any other row in the card.
 *
 * NO HEADING ABOVE THE LEVELS. An earlier version printed the field's own label there for context,
 * which was wrong twice over: the level names already say what was asked, and a Drop Down whose Title
 * was never filled has no usable label to print — it fell back to the raw `drop-down` field type,
 * putting a machine string at the top of a customer-facing record. The rows now sit inline with every
 * other row in the card, which is also how they line up with them.
 */
function FieldAnswer({ field, value }: { field: RenderableField; value: unknown }) {
  if (field.config.cascade) {
    const { path } = asCascade(value);
    if (path.length > 0) {
      return (
        <>
          {path.map((chosen, index) => (
            <KeyValueRow
              key={chosen.id}
              // Rendered from the ANSWER, not the current config: the level names and labels were
              // captured when the applicant answered, so this shows what was on their screen even if
              // the cascade has since been relabelled. Reading it back out of the config would
              // quietly rewrite history.
              label={`${chosen.level.trim() || `Level ${index + 1}`}:`}
              value={chosen.label}
            />
          ))}
        </>
      );
    }
  }

  return (
    <KeyValueRow
      label={`${field.label.trim() || field.fieldType}:`}
      value={<AnswerValue field={field} value={value} />}
    />
  );
}

/**
 * One answer as readable text, or a muted "Not answered".
 *
 * Unanswered questions are still LISTED. Omitting them would make an optional question the applicant
 * skipped indistinguishable from one that was never asked, which is exactly the distinction a
 * reviewer needs.
 */
function AnswerValue({ field, value }: { field: RenderableField; value: unknown }) {
  const rendered = describe(field, value);
  if (rendered === null) {
    return <span className="font-normal text-placeholder">Not answered</span>;
  }
  return rendered;
}

function describe(field: RenderableField, value: unknown): React.ReactNode | null {
  switch (field.fieldType) {
    case 'consent-text': {
      // Meaningful in BOTH directions — "declined" is a real recorded answer, not an absence — so
      // false renders rather than falling through to "Not answered".
      const agreed = asBool(value);
      return (
        <span className={agreed ? 'text-green00' : 'text-orangeDA'}>
          {agreed ? 'Agreed' : 'Not agreed'}
        </span>
      );
    }

    case 'single-choice': {
      // Tolerant of the one-element ARRAY an earlier build wrote — see asSingleChoice. Without this,
      // an answered single-choice read back as "Not answered".
      const chosen = asSingleChoice(value);
      return chosen.trim().length === 0 ? null : chosen;
    }

    case 'multiple-choice': {
      const chosen = asChoices(value);
      return chosen.length === 0 ? null : chosen.join(', ');
    }

    case 'date': {
      const text = asText(value);
      // Formatted, not raw ISO: the whole session view reads "14 Aug 2026", and one card showing
      // "2026-08-14" is the inconsistency the shared formatter exists to remove.
      return text.length === 0 ? null : formatDate(text);
    }

    case 'date-range': {
      const { from, to } = asDateRange(value);
      if (from.length === 0 && to.length === 0) return null;
      return `${from ? formatDate(from) : '—'} → ${to ? formatDate(to) : '—'}`;
    }

    case 'address': {
      const answer = asAddress(value);
      const lines = addressSubfieldsOf(field.config)
        .map((sub) => answer[sub.id]?.trim())
        .filter((line): line is string => Boolean(line));
      return lines.length === 0 ? null : lines.join(', ');
    }

    case 'upload': {
      // File NAMES only. The bytes were offloaded to object storage on submit, so the stored value
      // is a key — the files themselves appear in the step's artefact gallery below, which already
      // presigns and displays them.
      //
      // A LIST, because one Upload field can hold several files. `asUploads` also reads the single
      // object earlier sessions stored, so those keep rendering unchanged.
      const files = asUploads(value);
      if (files.length === 0) return null;
      return (
        <span className="flex flex-col gap-0.5">
          {files.map((file, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: position is the only identity a file list has.
            <span key={index}>{file.name}</span>
          ))}
        </span>
      );
    }

    case 'drop-down': {
      // An ANSWERED cascade never reaches here — `FieldAnswer` renders it as one row per level. Only
      // an unanswered one falls through, and null is exactly right for that.
      if (field.config.cascade) return null;

      const selected = asText(value).trim();
      return selected.length === 0 ? null : selected;
    }

    default: {
      const text = asText(value).trim();
      return text.length === 0 ? null : text;
    }
  }
}
